import { useState, useMemo } from "react";
import { Calendar as CalendarIcon, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { toast } from "sonner";
import api from "../lib/api";
import { PORTUGAL_CITIES } from "../lib/cities";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { cn } from "../lib/utils";

export default function ReservationForm({ content, items }) {
  const sec = content?.form_section || {};
  const brand = content?.brand || {};
  const [form, setForm] = useState({ name:"", email:"", phone:"", event_date:null, item_slug:"", location:"", message:"", people:"" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [openItem, setOpenItem] = useState(false);
  const [openLoc, setOpenLoc] = useState(false);
  const itemNameMap = useMemo(() => { const m = {}; items.forEach((i) => (m[i.slug] = i.name)); return m; }, [items]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.event_date || !form.item_slug || !form.location) {
      toast.error("Preencha todos os campos obrigatórios"); return;
    }
    setSubmitting(true);
    try {
      await api.upsertReservation({ name:form.name, email:form.email, phone:form.phone,
        event_date: format(form.event_date, "dd/MM/yyyy", { locale: pt }),
        item_slug: form.item_slug, item_name: itemNameMap[form.item_slug] || form.item_slug,
        location: form.location, message: form.message, people: form.people ? parseInt(form.people) : null, status: "pending" });
      if (brand.email) {
        const emailRes = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name:form.name, email:form.email, phone:form.phone, event_date:format(form.event_date,"dd/MM/yyyy",{locale:pt}), item_name:itemNameMap[form.item_slug]||form.item_slug, location:form.location, message:form.message, people:form.people, owner_email:brand.email }),
        });
        const emailData = await emailRes.json();
        console.log("Email response:", emailRes.status, emailData);
      }
      setSuccess(true); toast.success("Pedido recebido!");
      setForm({ name:"", email:"", phone:"", event_date:null, item_slug:"", location:"", message:"", people:"" });
    } catch (err) { toast.error(err?.message || "Erro ao enviar pedido"); }
    finally { setSubmitting(false); }
  };

  if (success) return (
    <section id="reservar" className="relative py-20 md:py-28 overflow-hidden" style={{backgroundColor:"var(--ff-cream)"}} data-testid="reservation-section">
      <div className="absolute inset-0 bg-stripes-blue-soft opacity-30" />
      <div className="relative max-w-3xl mx-auto px-5 md:px-8">
        <div className="rounded-3xl bg-white p-10 text-center border-4" style={{borderColor:"var(--ff-peach)"}} data-testid="reservation-success">
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-5" style={{background:"var(--ff-peach)"}}><Check size={40} className="text-white" /></div>
          <h3 className="title-pop text-3xl md:text-4xl mb-3" style={{color:"var(--ff-text)"}}>{sec.success_title || "Pedido recebido!"}</h3>
          <p className="font-body text-base md:text-lg mb-6" style={{color:"var(--ff-text-soft)"}}>{sec.success_message}</p>
          <button type="button" onClick={() => setSuccess(false)} className="ff-btn ff-btn-secondary" data-testid="reservation-new">Nova Reserva</button>
        </div>
      </div>
    </section>
  );

  return (
    <section id="reservar" className="relative py-20 md:py-28 overflow-hidden" style={{backgroundColor:"var(--ff-cream)"}} data-testid="reservation-section">
      <div className="absolute inset-0 bg-stripes-blue-soft opacity-30" />
      <div className="relative max-w-3xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10 md:mb-12">
          <span className="inline-block font-display text-sm font-semibold tracking-[0.2em] uppercase mb-4" style={{color:"var(--ff-peach)"}}>{sec.eyebrow || "Reservar"}</span>
          <h2 className="title-pop text-4xl md:text-6xl mb-4" style={{color:"var(--ff-text)"}}>{sec.title}</h2>
          <p className="max-w-xl mx-auto text-base md:text-lg font-body" style={{color:"var(--ff-text-soft)"}}>{sec.subtitle}</p>
        </div>
        <form onSubmit={submit} className="rounded-3xl bg-white p-6 md:p-10 border-4 space-y-5 shadow-lg" style={{borderColor:"var(--ff-peach)"}} data-testid="reservation-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div><label className="ff-label">Nome *</label><input type="text" className="ff-input" placeholder="Nome" value={form.name} onChange={e=>update("name",e.target.value)} required /></div>
            <div><label className="ff-label">Email *</label><input type="email" className="ff-input" placeholder="Email" value={form.email} onChange={e=>update("email",e.target.value)} required /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div><label className="ff-label">Telefone *</label><input type="tel" className="ff-input" placeholder="+351 ..." value={form.phone} onChange={e=>update("phone",e.target.value)} required /></div>
            <div>
              <label className="ff-label">Data *</label>
              <Popover><PopoverTrigger asChild><button type="button" className={cn("ff-input flex items-center justify-between text-left",!form.event_date&&"text-slate-400")}>{form.event_date?format(form.event_date,"dd/MM/yyyy",{locale:pt}):"Selecione"}<CalendarIcon size={18} className="opacity-60" /></button></PopoverTrigger><PopoverContent className="p-0 w-auto" align="start"><Calendar mode="single" selected={form.event_date} onSelect={d=>update("event_date",d)} disabled={d=>d<new Date(new Date().toDateString())} locale={pt} initialFocus /></PopoverContent></Popover>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div><label className="ff-label">Pessoas *</label><input type="number" className="ff-input" placeholder="Quantidade" min="1" value={form.people} onChange={e=>update("people",e.target.value)} required /></div>
            <div>
              <label className="ff-label">Item</label>
              <Popover open={openItem} onOpenChange={setOpenItem}>
                <PopoverTrigger asChild><button type="button" className={cn("ff-input flex items-center justify-between text-left",!form.item_slug&&"text-slate-400")}>{form.item_slug?itemNameMap[form.item_slug]:"Selecione"}<ChevronsUpDown size={16} className="opacity-60" /></button></PopoverTrigger>
                <PopoverContent className="p-0" align="start"><Command><CommandList><CommandGroup>{items.map(it=>(<CommandItem key={it.slug} value={it.name} onSelect={()=>{update("item_slug",it.slug);setOpenItem(false)}}><Check className={cn("mr-2 h-4 w-4",form.item_slug===it.slug?"opacity-100":"opacity-0")} />{it.name}</CommandItem>))}</CommandGroup></CommandList></Command></PopoverContent></Popover>
            </div>
            <div><label className="ff-label">Local</label><input className="ff-input" placeholder="Local" value={form.location} onChange={e=>update("location",e.target.value)} /></div>
          </div>
          <div><label className="ff-label">Mensagem</label><textarea className="ff-input min-h-[120px]" placeholder="Mensagem" value={form.message} onChange={e=>update("message",e.target.value)} /></div>
          <div className="text-center pt-2">
            <button type="submit" disabled={submitting} className="ff-btn ff-btn-primary disabled:opacity-60" data-testid="form-submit-button">
              {submitting?<Loader2 className="animate-spin mr-2" size={20}/>:null}{submitting?"A enviar...":"Verificar Disponibilidade"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}